import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { sendApiRequest } from '../utils';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  FiDollarSign,
  FiCalendar,
  FiFilter,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiInfo,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiDownload,
  FiEye,
  FiBank,
  FiCreditCard,
  FiCode,
  FiMapPin,
  FiTrendingDown
} from 'react-icons/fi';
import {
  FaWallet,
  FaHandHoldingUsd,
  FaChartLine,
  FaBank,
  FaReceipt,
  FaFileInvoice,
  FaPiggyBank
} from 'react-icons/fa';

const WithdrawalRequestList = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchParams, setSearchParams] = useState({
    status: 'all',
    startDate: null,
    endDate: null
  });

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        ...(searchParams.status !== 'all' && { status: searchParams.status }),
        ...(searchParams.startDate && { startDate: searchParams.startDate.toISOString() }),
        ...(searchParams.endDate && { endDate: searchParams.endDate.toISOString() })
      });

      const response = await sendApiRequest(`api/withdrawal-requests/user?${params}`, {}, 'GET');
      
      if (response.status === 200) {
        setRequests(response.data.withdrawalRequests || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalRecords(response.data.total || 0);
      } else {
        toast.error(response.data?.message || 'Failed to fetch withdrawal requests');
        setRequests([]);
        setTotalPages(1);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error(error.message || 'An error occurred while fetching requests');
      setRequests([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [currentPage, itemsPerPage, searchParams.status, searchParams.startDate, searchParams.endDate]);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date, field) => {
    setSearchParams(prev => ({ ...prev, [field]: date }));
  };

  const handleReset = () => {
    setSearchParams({
      status: 'all',
      startDate: null,
      endDate: null
    });
    setCurrentPage(1);
  };

  const openRequestDetails = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
  };

  const formatAmount = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} Lakh`;
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusConfig = (status) => {
    switch(status) {
      case 'approved':
        return { 
          icon: <FiCheckCircle className="w-4 h-4" />, 
          color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
          label: 'Approved'
        };
      case 'pending':
        return { 
          icon: <FiClock className="w-4 h-4" />, 
          color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
          label: 'Pending'
        };
      case 'rejected':
        return { 
          icon: <FiXCircle className="w-4 h-4" />, 
          color: 'bg-red-100 text-red-700 border-red-200',
          label: 'Rejected'
        };
      default:
        return { 
          icon: <FiInfo className="w-4 h-4" />, 
          color: 'bg-gray-100 text-gray-700 border-gray-200',
          label: status
        };
    }
  };

  // Calculate statistics
  const totalAmount = requests.reduce((sum, r) => sum + r.amount, 0);
  const approvedAmount = requests.filter(r => r.status === 'approved').reduce((sum, r) => sum + r.amount, 0);
  const pendingAmount = requests.filter(r => r.status === 'pending').reduce((sum, r) => sum + r.amount, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Withdrawal Requests
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">Track and manage your withdrawal history</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchRequests}
                className="p-2.5 bg-white rounded-xl shadow-md hover:shadow-lg transition text-gray-600 hover:text-indigo-600"
                title="Refresh"
              >
                <FiRefreshCw className="w-5 h-5" />
              </button>
              <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-semibold text-emerald-700">Live Data</span>
              </span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Requests</p>
                <p className="text-3xl font-bold text-gray-800">{totalRecords}</p>
                <p className="text-xs text-gray-400 mt-1">Total withdrawal requests</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                <FaReceipt className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-2xl font-bold text-indigo-600">{formatAmount(totalAmount)}</p>
                <p className="text-xs text-gray-400 mt-1">Requested amount</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
                <FaWallet className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved Amount</p>
                <p className="text-2xl font-bold text-emerald-600">{formatAmount(approvedAmount)}</p>
                <p className="text-xs text-gray-400 mt-1">Successfully approved</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                <FiCheckCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Amount</p>
                <p className="text-2xl font-bold text-amber-600">{formatAmount(pendingAmount)}</p>
                <p className="text-xs text-gray-400 mt-1">Awaiting approval</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                <FiClock className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search Filters */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <FiFilter className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold text-gray-800">Filter Requests</h3>
            </div>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  name="status"
                  value={searchParams.status}
                  onChange={handleSearchChange}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <div className="relative">
                  <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <DatePicker
                    selected={searchParams.startDate}
                    onChange={(date) => handleDateChange(date, 'startDate')}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select start date"
                    maxDate={searchParams.endDate || new Date()}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <div className="relative">
                  <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <DatePicker
                    selected={searchParams.endDate}
                    onChange={(date) => handleDateChange(date, 'endDate')}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select end date"
                    minDate={searchParams.startDate}
                    maxDate={new Date()}
                  />
                </div>
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={handleReset}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-4 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Showing {requests.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} to{' '}
            {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} entries
          </p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="px-2 py-1 border border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
            <span className="text-sm text-gray-500">entries</span>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Bank Details</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Request Date</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <AnimatePresence>
                  {loading ? (
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <td colSpan="6" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="relative">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <FaHandHoldingUsd className="w-5 h-5 text-indigo-400 animate-pulse" />
                            </div>
                          </div>
                          <p className="mt-4 text-gray-500 font-medium">Loading withdrawal requests...</p>
                        </div>
                      </td>
                    </motion.tr>
                  ) : requests.length === 0 ? (
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <td colSpan="6" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <FiDollarSign className="w-10 h-10 text-gray-400" />
                          </div>
                          <p className="text-gray-500 font-medium text-lg">No withdrawal requests found</p>
                          <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
                        </div>
                      </td>
                    </motion.tr>
                  ) : (
                    requests.map((request, index) => {
                      const statusConfig = getStatusConfig(request.status);
                      return (
                        <motion.tr
                          key={request._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ delay: index * 0.03 }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                            {((currentPage - 1) * itemsPerPage) + index + 1}
                           </td>
                          <td className="px-4 py-4">
                            <div className="space-y-1">
                              <p className="text-sm font-bold text-gray-800">{formatAmount(request.amount)}</p>
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <FiTrendingDown className="w-3 h-3" />
                                <span>Net: {formatAmount(request.deductions?.netAmount)}</span>
                              </div>
                            </div>
                           </td>
                          <td className="px-4 py-4">
                            <div className="space-y-1 text-sm">
                              <p className="text-gray-700">{request.bankDetails?.bankName}</p>
                              <p className="text-xs text-gray-500">****{request.bankDetails?.accountNumber?.slice(-4)}</p>
                            </div>
                           </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {new Date(request.createdAt).toLocaleDateString('en-IN')}
                           </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${statusConfig.color}`}>
                              {statusConfig.icon}
                              {statusConfig.label}
                            </span>
                           </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <button
                              onClick={() => openRequestDetails(request)}
                              className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-600 transition"
                              title="View Details"
                            >
                              <FiEye className="w-4 h-4" />
                            </button>
                           </td>
                        </motion.tr>
                      );
                    })
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-gray-100 px-6 py-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Request Details Modal */}
      <AnimatePresence>
        {showModal && selectedRequest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={closeModal}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 sticky top-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl">
                      <FaFileInvoice className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-white font-semibold text-lg">Withdrawal Request Details</h2>
                      <p className="text-indigo-100 text-sm">Request ID: {selectedRequest._id?.slice(-8)}</p>
                    </div>
                  </div>
                  <button onClick={closeModal} className="text-white hover:bg-white/20 rounded-lg p-1 transition">
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-5">
                {/* Amount Section */}
                <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100">
                  <h3 className="text-sm font-semibold text-indigo-800 mb-3">Amount Details</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Requested Amount</span>
                      <span className="font-bold text-gray-800">{formatAmount(selectedRequest.amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Admin Fee (5%)</span>
                      <span className="text-gray-700">{formatAmount(selectedRequest.deductions?.adminFee)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">TDS (5%)</span>
                      <span className="text-gray-700">{formatAmount(selectedRequest.deductions?.tds)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-indigo-200">
                      <span className="font-semibold text-gray-800">Net Amount</span>
                      <span className="text-xl font-bold text-emerald-600">{formatAmount(selectedRequest.deductions?.netAmount)}</span>
                    </div>
                  </div>
                </div>

                {/* Status Section */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-800">Status Information</h3>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getStatusConfig(selectedRequest.status).color}`}>
                      {getStatusConfig(selectedRequest.status).icon}
                      {getStatusConfig(selectedRequest.status).label}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Request Date</span>
                      <span className="text-gray-700">{new Date(selectedRequest.createdAt).toLocaleString()}</span>
                    </div>
                    {selectedRequest.updatedAt && selectedRequest.updatedAt !== selectedRequest.createdAt && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Last Updated</span>
                        <span className="text-gray-700">{new Date(selectedRequest.updatedAt).toLocaleString()}</span>
                      </div>
                    )}
                    {selectedRequest.adminNote && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-sm font-semibold text-gray-700 mb-1">Admin Note</p>
                        <p className="text-sm text-gray-600">{selectedRequest.adminNote}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bank Details Section */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FaPiggyBank className="w-4 h-4 text-indigo-500" />
                    Bank Account Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Bank Name</span>
                      <span className="font-medium text-gray-800">{selectedRequest.bankDetails?.bankName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Account Number</span>
                      <span className="font-mono text-gray-800">{selectedRequest.bankDetails?.accountNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">IFSC Code</span>
                      <span className="font-mono text-gray-800">{selectedRequest.bankDetails?.ifscCode}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Branch Name</span>
                      <span className="text-gray-800">{selectedRequest.bankDetails?.branchName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Account Type</span>
                      <span className="text-gray-800">{selectedRequest.bankDetails?.accountType}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WithdrawalRequestList;